import { usePostOffersCargos } from "@/entities/post-offers-cargos/fetch";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import { isSuccessStatusCode } from "@/shared/lib/http";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import type { DraftOfferCargo, ShipperCargoCreatePageProps } from "../model";

const concatAddress = (draftOfferCargo: DraftOfferCargo) => {
  const destinations = draftOfferCargo.destinations.map((destination) => ({
    ...destination,
    city: destination.city,
    addressLine: destination.addressLine,
    address: destination.addressLine
      ? `${destination.city} ${destination.addressLine}`
      : destination.city,
  }));
  return { ...draftOfferCargo, destinations };
};

export const useShipperCargoCreatePageProps =
  (): ShipperCargoCreatePageProps => {
    // State
    const [tab, setTab] = useState<"easy" | "normal">("easy");
    const [previewValues, setPreviewValues] =
      useState<ShipperCargoCreatePageProps["previewValues"]>(null);

    // Local Storage
    // 記入途中の情報をローカルストレージに保存する
    const [inProgressValues, setInProgressValues, removeInProgressValues] =
      useInProgressValuesLocalCache<
        ShipperCargoCreatePageProps["defaultInputValues"]
      >("ShipperCargoCreatePage");

    // Backend API
    const postOffersCargos = usePostOffersCargos();

    // Global Notification
    const { setGlobalNotification } = useGlobalNotification();

    // Actions
    const navigate = useNavigate();
    const onTabChange = useCallback<ShipperCargoCreatePageProps["onTabChange"]>(
      (newValue) => {
        setTab(newValue);
      },
      [],
    );

    const onInputValuesUpdate = useCallback<
      ShipperCargoCreatePageProps["onInputValuesUpdate"]
    >(
      (draftOfferCargo) => {
        setInProgressValues(draftOfferCargo);
      },
      [setInProgressValues],
    );

    const onConfirmClick = useCallback<
      ShipperCargoCreatePageProps["onConfirmClick"]
    >(async (draftOfferCargo) => {
      setPreviewValues(draftOfferCargo);
    }, []);

    const onBackToInProgressClick = useCallback<
      ShipperCargoCreatePageProps["onBackToInProgressClick"]
    >(() => {
      setPreviewValues(null);
    }, []);

    const onSubmitClick = useCallback<
      ShipperCargoCreatePageProps["onSubmitClick"]
    >(
      async (draftOfferCargo) => {
        const response = await postOffersCargos.mutateAsync({
          data: { ...concatAddress(draftOfferCargo), vehicleId: null },
        });
        if (response && isSuccessStatusCode(response.status)) {
          setGlobalNotification({
            variant: "success",
            message: "荷物の新規登録が完了しました",
            autoHide: true,
          });
          removeInProgressValues();
          navigate({
            to: "/shipper/cargos",
          });
        }
      },
      [
        postOffersCargos.mutateAsync,
        removeInProgressValues,
        setGlobalNotification,
        navigate,
      ],
    );

    return {
      tab,
      isEasyForm: tab === "easy",
      defaultInputValues: inProgressValues,
      previewValues,
      onTabChange,
      onInputValuesUpdate,
      onBackToInProgressClick,
      onSubmitClick,
      onConfirmClick,
    };
  };
